export type AllocatableInvoice = {
  id: string;
  dueAt: Date;
  amountOpen: number;
};

export type InvoicePaymentAllocation = {
  id: string;
  amount: number;
};

/** Round to two decimal places (cents) to avoid floating-point drift. */
export function roundCents(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Allocate `amount` across `invoices` oldest-first, paying each up to its
 * `amountOpen`. Zero-allocation invoices are omitted. The input array is
 * not mutated. All arithmetic is rounded to the cent to prevent
 * floating-point drift from producing incorrect totals.
 */
export function allocateInvoicePayments(
  invoices: readonly AllocatableInvoice[],
  amount: number,
): InvoicePaymentAllocation[] {
  if (amount <= 0 || invoices.length === 0) return [];
  const sorted = [...invoices].sort(
    (a, b) => a.dueAt.getTime() - b.dueAt.getTime(),
  );
  const out: InvoicePaymentAllocation[] = [];
  let remaining = roundCents(amount);
  for (const inv of sorted) {
    if (remaining <= 0) break;
    const alloc = roundCents(Math.min(remaining, inv.amountOpen));
    if (alloc > 0) {
      out.push({ id: inv.id, amount: alloc });
      remaining = roundCents(remaining - alloc);
    }
  }
  return out;
}
